import React, { useState, useEffect } from 'react';
import { FileText, Download, AlertCircle, CheckCircle, Loader, Calendar, Building, MapPin, Phone, Eye, X, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Set worker path for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface CertificateOfEffortGenerationProps {
  documents: any[];
  clientId: string;
  clientName: string;
  projectId?: string;
  onComplete: (result: any) => void;
  loading: boolean;
}

interface InsurerContact {
  insurer: string;
  representative: string;
  telephoneNo: string;
  date: string;
  resultCode: 'A' | 'B' | 'C';
}

interface CertificateData {
  nameOfInsured: string;
  addressOfInsured: string;
  locationOfProperty: string;
  insuranceCoverage: string;
  originatingProducerCorporate: string;
  originatingProducerIndividual: string;
  originatingProducerAddress: string;
  dateEngaged: string;
  year: string;
  insurerContacts: InsurerContact[];
}

const CertificateOfEffortGeneration: React.FC<CertificateOfEffortGenerationProps> = ({
  documents,
  clientId,
  clientName,
  projectId,
  onComplete,
  loading: parentLoading
}) => {
  const { token, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [certificateData, setCertificateData] = useState<CertificateData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [originalPdfData, setOriginalPdfData] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [step, setStep] = useState<'analyze' | 'review' | 'generate' | 'complete'>('analyze');

  // Auto-start analysis when component mounts and token is available
  useEffect(() => {
    if (documents.length > 0 && token && !authLoading) {
      analyzeDocuments();
    }
  }, [documents, token, authLoading]);

  const analyzeDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Analyzing documents for Certificate of Effort...');
      console.log('🔐 Token available:', !!token);
      
      if (!token) {
        console.error('❌ Authentication token not available');
        throw new Error('Authentication token not found');
      }

      const payload = {
        documents: documents.map(doc => ({
          id: doc.id,
          name: doc.name,
          extractedText: doc.extractedText || doc.text
        })),
        clientId,
        clientName,
        projectId,
        workflowType: 'certificate_of_effort'
      };

      const response = await fetch(`${API_BASE_URL}/api/workflows/certificate-of-effort/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze documents');
      }

      const result = await response.json();
      console.log('📋 Certificate data extracted:', result);
      
      setCertificateData(result.certificateData);
      setStep('review');
      
    } catch (error) {
      console.error('Error analyzing documents:', error);
      setError(error instanceof Error ? error.message : 'Failed to analyze documents');
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      
      console.log('📄 Generating PDF Certificate...');
      console.log('🔐 Token available for PDF generation:', !!token);
      
      if (!token) {
        console.error('❌ Authentication token not available for PDF generation');
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_BASE_URL}/api/workflows/certificate-of-effort/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          certificateData,
          clientId,
          projectId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const result = await response.json();
      console.log('✅ PDF generated successfully:', result);
      
      // Create a downloadable blob URL from the base64 data
      if (result.downloadUrl && result.downloadUrl.startsWith('data:application/pdf;base64,')) {
        const base64Data = result.downloadUrl.split(',')[1];
        const binaryData = atob(base64Data);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        setDownloadUrl(blobUrl);
        setOriginalPdfData(result.downloadUrl); // Store the original base64 data
      } else {
        setDownloadUrl(result.downloadUrl);
        setOriginalPdfData(result.downloadUrl);
      }
      
      // Show preview modal immediately after generation
      setShowPreview(true);
      setStep('complete');
      // Don't call onComplete immediately - let user close preview first
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  const updateCertificateData = (field: keyof CertificateData, value: any) => {
    setCertificateData(prev => prev ? { ...prev, [field]: value } : null);
  };

  const addInsurerContact = () => {
    if (!certificateData) return;
    
    const newContact: InsurerContact = {
      insurer: '',
      representative: '',
      telephoneNo: '',
      date: '',
      resultCode: 'A'
    };
    
    updateCertificateData('insurerContacts', [...(certificateData.insurerContacts || []), newContact]);
  };

  const updateInsurerContact = (index: number, field: keyof InsurerContact, value: string) => {
    if (!certificateData) return;
    
    const updatedContacts = [...(certificateData.insurerContacts || [])];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    updateCertificateData('insurerContacts', updatedContacts);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    // Call onComplete when user closes the preview to complete the workflow
    onComplete({ 
      success: true, 
      message: 'Certificate of Effort generated successfully!',
      certificateData,
      downloadUrl: downloadUrl, // Temporary blob URL for immediate use
      originalPdfData: originalPdfData // Original base64 data for permanent storage
    });
  };

  const removeInsurerContact = (index: number) => {
    if (!certificateData) return;
    
    const updatedContacts = (certificateData.insurerContacts || []).filter((_, i) => i !== index);
    updateCertificateData('insurerContacts', updatedContacts);
  };


  if (loading || parentLoading || authLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <Loader className="h-8 w-8 animate-spin text-primary" />
        <p className="text-gray-600 dark:text-gray-400">
          Analyzing documents and extracting certificate information...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-center space-x-2 text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">Error</span>
        </div>
        <p className="mt-2 text-red-600 dark:text-red-300">{error}</p>
        <button
          onClick={analyzeDocuments}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Retry Analysis
        </button>
      </div>
    );
  }

  // PDF Preview Modal
  const PDFPreviewModal = () => {
    if (!showPreview || !downloadUrl) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Certificate of Effort Preview
            </h3>
            <div className="flex items-center space-x-4">
              {numPages && numPages > 1 && (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      console.log('Previous clicked, current page:', pageNumber);
                      setPageNumber(prev => {
                        const newPage = Math.max(1, prev - 1);
                        console.log('Setting page to:', newPage);
                        return newPage;
                      });
                    }}
                    disabled={pageNumber <= 1}
                    className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded">
                    Page {pageNumber} of {numPages}
                  </span>
                  <button
                    onClick={() => {
                      console.log('Next clicked, current page:', pageNumber, 'total pages:', numPages);
                      console.log('Button disabled?', pageNumber >= (numPages || 1));
                      setPageNumber(prev => {
                        const newPage = Math.min(numPages || 1, prev + 1);
                        console.log('Previous page:', prev, 'Setting page to:', newPage);
                        return newPage;
                      });
                    }}
                    disabled={!numPages || pageNumber >= numPages}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      !numPages || pageNumber >= numPages
                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-200 cursor-pointer'
                    }`}
                  >
                    Next ({pageNumber}/{numPages || 0})
                  </button>
                </div>
              )}
              <button
                onClick={handleClosePreview}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="p-4 overflow-auto max-h-[70vh] flex justify-center">
            <div className="max-w-full">
              <Document
                key={`document-${pageNumber}`}
                file={downloadUrl}
                onLoadSuccess={({ numPages }) => {
                  console.log('PDF loaded successfully with', numPages, 'pages');
                  setNumPages(numPages);
                  if (pageNumber > numPages) {
                    setPageNumber(1);
                  }
                  // Force a re-render to ensure navigation buttons appear
                  setTimeout(() => {
                    console.log('PDF state after load - numPages:', numPages, 'pageNumber:', pageNumber);
                    console.log('Navigation should be visible for multi-page PDF:', numPages > 1);
                  }, 100);
                }}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                }}
                loading={
                  <div className="flex items-center justify-center py-8">
                    <Loader className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600 dark:text-gray-400">Loading PDF...</span>
                  </div>
                }
                error={
                  <div className="flex flex-col items-center justify-center py-8 text-red-600">
                    <AlertCircle className="h-8 w-8 mb-2" />
                    <span>Failed to load PDF. Please try downloading instead.</span>
                  </div>
                }
              >
                <Page 
                  pageNumber={pageNumber} 
                  scale={1.0}
                  width={600}
                  loading={
                    <div className="flex items-center justify-center py-8">
                      <Loader className="h-6 w-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading page {pageNumber}...</span>
                    </div>
                  }
                  error={
                    <div className="flex flex-col items-center justify-center py-8 text-red-600">
                      <AlertCircle className="h-6 w-6 mb-2" />
                      <span>Failed to load page {pageNumber}</span>
                      <button
                        onClick={() => setPageNumber(1)}
                        className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded text-sm"
                      >
                        Go to page 1
                      </button>
                    </div>
                  }
                  onLoadSuccess={(page) => {
                    console.log(`Page ${pageNumber} loaded successfully - dimensions:`, page.width, 'x', page.height);
                  }}
                  onLoadError={(error) => {
                    console.error(`Failed to load page ${pageNumber}:`, error);
                  }}
                />
              </Document>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClosePreview}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Close Preview
            </button>
            <button
              onClick={() => {
                if (downloadUrl) {
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = 'certificate-of-effort.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  // Close preview and complete workflow after download
                  setTimeout(() => handleClosePreview(), 100);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <PDFPreviewModal />
      
      {step === 'analyze' && (
        <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Certificate of Effort Generation</h2>
          
          <div className="space-y-6">
            <div className="flex items-center space-x-3 text-blue-600 dark:text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="font-medium">Analyzing documents...</span>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                We're analyzing your uploaded documents to extract relevant information for the Certificate of Effort.
                This process may take a few moments.
              </p>
            </div>
          </div>
        </div>
      )}

      {step === 'review' && certificateData && (
        <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Review Certificate Information</h2>
          
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-blue-700 dark:text-blue-400">
                <FileText className="h-5 w-5" />
                <span className="font-medium">Certificate Information Extracted</span>
              </div>
              <p className="mt-2 text-blue-600 dark:text-blue-300">
                Please review and edit the extracted information before generating the PDF.
              </p>
            </div>

            {/* Insured Information */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <Building className="h-5 w-5 mr-2" />
                Insured Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name of Insured
                  </label>
                  <input
                    type="text"
                    value={certificateData.nameOfInsured || ''}
                    onChange={(e) => updateCertificateData('nameOfInsured', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Address of Insured
                  </label>
                  <textarea
                    value={certificateData.addressOfInsured || ''}
                    onChange={(e) => updateCertificateData('addressOfInsured', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location of Property or Risk
                  </label>
                  <textarea
                    value={certificateData.locationOfProperty || ''}
                    onChange={(e) => updateCertificateData('locationOfProperty', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Insurance Coverage: Description and Amount
                  </label>
                  <textarea
                    value={certificateData.insuranceCoverage || ''}
                    onChange={(e) => updateCertificateData('insuranceCoverage', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Producer Information */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Originating Producer Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Corporate or Partnership Name
                  </label>
                  <input
                    type="text"
                    value={certificateData.originatingProducerCorporate || ''}
                    onChange={(e) => updateCertificateData('originatingProducerCorporate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Individual Name and/or Title
                  </label>
                  <input
                    type="text"
                    value={certificateData.originatingProducerIndividual || ''}
                    onChange={(e) => updateCertificateData('originatingProducerIndividual', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Complete Address
                  </label>
                  <textarea
                    value={certificateData.originatingProducerAddress || ''}
                    onChange={(e) => updateCertificateData('originatingProducerAddress', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date Engaged
                    </label>
                    <input
                      type="date"
                      value={certificateData.dateEngaged || ''}
                      onChange={(e) => updateCertificateData('dateEngaged', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Year
                    </label>
                    <input
                      type="text"
                      value={certificateData.year || ''}
                      onChange={(e) => updateCertificateData('year', e.target.value)}
                      placeholder="2024"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Insurer Contacts */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                  <Phone className="h-5 w-5 mr-2" />
                  Insurer Contacts
                </h3>
                <button
                  onClick={addInsurerContact}
                  className="px-3 py-1 bg-primary text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  Add Contact
                </button>
              </div>
              
              <div className="space-y-4">
                {(certificateData.insurerContacts || []).map((contact, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">Contact #{index + 1}</h4>
                      {(certificateData.insurerContacts || []).length > 1 && (
                        <button
                          onClick={() => removeInsurerContact(index)}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Insurer ({(contact.insurer || '').length}/35)
                        </label>
                        <input
                          type="text"
                          value={contact.insurer || ''}
                          onChange={(e) => updateInsurerContact(index, 'insurer', e.target.value)}
                          maxLength={35}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="text-right">
                          <span className={`text-xs ${(contact.insurer || '').length > 28 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
                            {(contact.insurer || '').length}/35 characters
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Representative ({(contact.representative || '').length}/35)
                        </label>
                        <input
                          type="text"
                          value={contact.representative || ''}
                          onChange={(e) => updateInsurerContact(index, 'representative', e.target.value)}
                          maxLength={35}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="text-right">
                          <span className={`text-xs ${(contact.representative || '').length > 28 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
                            {(contact.representative || '').length}/35 characters
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Telephone No.
                        </label>
                        <input
                          type="tel"
                          value={contact.telephoneNo}
                          onChange={(e) => updateInsurerContact(index, 'telephoneNo', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date
                        </label>
                        <input
                          type="date"
                          value={contact.date}
                          onChange={(e) => updateInsurerContact(index, 'date', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Result Code
                        </label>
                        <select
                          value={contact.resultCode}
                          onChange={(e) => updateInsurerContact(index, 'resultCode', e.target.value as 'A' | 'B' | 'C')}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="A">A - Received quotation</option>
                          <option value="B">B - Requested quotation but did not receive</option>
                          <option value="C">C - Not contacted</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-6">
              <button
                onClick={() => setStep('analyze')}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Back to Analysis
              </button>
              <button
                onClick={generatePdf}
                disabled={isGenerating}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isGenerating ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    <span>Generating PDF...</span>
                  </>
                ) : (
                  <span>Generate Certificate PDF</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'generate' && (
        <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Generating Certificate</h2>
          
          <div className="space-y-6">
            <div className="flex items-center space-x-3 text-blue-600 dark:text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="font-medium">Creating your Certificate of Effort...</span>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Please wait while we generate your professional Certificate of Effort document.
              </p>
            </div>
          </div>
        </div>
      )}

      {step === 'complete' && downloadUrl && !showPreview && (
        <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Certificate Generated Successfully</h2>
          
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-800 dark:text-green-200 font-medium">
                  Your Certificate of Effort has been generated successfully!
                </p>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => setShowPreview(true)}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Preview Certificate
              </button>
              <a
                href={downloadUrl}
                download
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors inline-block"
              >
                Download Certificate
              </a>
              <button
                onClick={handleClosePreview}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CertificateOfEffortGeneration;